import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Layers, Plus, Edit, Trash2, Eye, Check, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_TEMPLATE = `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
</head>
<body style="margin:0; padding:0; background-color:#0a0a0a; font-family:Arial, Helvetica, sans-serif;">
<table width="100%" bgcolor="#0a0a0a" cellpadding="0" cellspacing="0">
<tr>
<td align="center">
<table width="600" cellpadding="0" cellspacing="0" style="background:#111111; margin:40px 0; border-radius:8px; overflow:hidden; border: 1px solid #222;">
<tr>
<td align="center" style="background:#111111; padding:30px 20px; border-bottom: 1px solid #222;">
<img src="https://customer-assets.emergentagent.com/job_mail-dispatch-18/artifacts/bs8dk0ju_IMG_5169.PNG" 
     alt="Delta AI Academy" 
     width="200" 
     style="display:block; margin-bottom:10px;">
</td>
</tr>
<tr>
<td style="padding:40px 30px; color:#e0e0e0; font-size:15px; line-height:1.7;">
<p style="margin-top:0; color:#ffffff;">Dear Valued Member,</p>
<p>Your message content goes here.</p>
<p style="margin-bottom:0;">
Best Regards,<br>
<strong style="color:#00d4aa;">Delta AI Academy Team</strong>
</p>
</td>
</tr>
<tr>
<td style="border-top:1px solid #222;"></td>
</tr>
<tr>
<td align="center" style="padding:25px 20px; font-size:12px; color:#666666;">
<p style="margin:0 0 10px 0;">© 2026 Delta AI Academy. All Rights Reserved.</p>
</td>
</tr>
</table>
</td>
</tr>
</table>
</body>
</html>`;

export const Templates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    subject: "",
    html_content: DEFAULT_TEMPLATE,
    is_default: false
  });

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/templates`);
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error("Failed to fetch templates:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const handleCreate = async () => {
    if (!formData.name || !formData.subject) {
      toast.error("Please fill in name and subject");
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/templates`, formData);
      toast.success("Template created successfully");
      setDialogOpen(false);
      setFormData({ name: "", subject: "", html_content: DEFAULT_TEMPLATE, is_default: false });
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create template");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedTemplate) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/templates/${selectedTemplate.id}`, formData);
      toast.success("Template updated successfully");
      setDialogOpen(false);
      setSelectedTemplate(null);
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update template");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("Are you sure you want to delete this template?")) return;
    
    try {
      await axios.delete(`${API}/templates/${id}`);
      toast.success("Template deleted");
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete template");
    }
  };

  const openEditDialog = (template) => {
    setSelectedTemplate(template);
    setFormData({
      name: template.name,
      subject: template.subject,
      html_content: template.html_content,
      is_default: template.is_default
    });
    setDialogOpen(true);
  };

  const openCreateDialog = () => {
    setSelectedTemplate(null);
    setFormData({ name: "", subject: "", html_content: DEFAULT_TEMPLATE, is_default: false });
    setDialogOpen(true);
  };

  const openPreview = (template) => {
    setSelectedTemplate(template);
    setPreviewOpen(true);
  };

  return (
    <div className="p-6 md:p-8 animate-fade-in" data-testid="templates-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold text-white tracking-tight">
            Email Templates
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Create and manage your email templates
          </p>
        </div>
        <Button
          onClick={openCreateDialog}
          className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg w-fit"
          data-testid="btn-create-template"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Template
        </Button>
      </div>

      {/* Templates Grid */}
      {loading ? (
        <div className="text-center py-12 text-slate-500">Loading templates...</div>
      ) : templates.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((template) => (
            <Card key={template.id} className="bg-[#111] border-[#1a1a1a] rounded-lg">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
                      {template.name}
                      {template.is_default && (
                        <span className="text-xs bg-cyan-500/20 text-cyan-400 px-2 py-0.5 rounded-full">
                          Default
                        </span>
                      )}
                    </CardTitle>
                    <p className="text-xs text-slate-500 mt-1 truncate">{template.subject}</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="bg-[#0a0a0a] rounded-lg p-3 h-32 overflow-hidden mb-4 border border-[#1a1a1a]">
                  <div 
                    className="text-xs text-slate-600 overflow-hidden"
                    dangerouslySetInnerHTML={{ __html: template.html_content?.slice(0, 500) }}
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openPreview(template)}
                    className="flex-1 border-[#222] text-slate-400 hover:text-white hover:bg-[#1a1a1a]"
                    data-testid={`btn-preview-${template.id}`}
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    Preview
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openEditDialog(template)}
                    className="flex-1 border-[#222] text-slate-400 hover:text-white hover:bg-[#1a1a1a]"
                    data-testid={`btn-edit-${template.id}`}
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(template.id)}
                    className="border-[#222] text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    data-testid={`btn-delete-${template.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="bg-[#111] border-[#1a1a1a] rounded-lg">
          <CardContent className="py-12 text-center">
            <Layers className="w-12 h-12 mx-auto text-slate-700 mb-3" />
            <p className="text-slate-500">No templates yet. Create your first template!</p>
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#111] border-[#1a1a1a] text-white max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {selectedTemplate ? "Edit Template" : "Create New Template"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-400">Template Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g., Welcome Email"
                  className="bg-[#0d0d0d] border-[#222] text-white"
                  data-testid="input-template-name"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-400">Subject Line</Label>
                <Input
                  value={formData.subject}
                  onChange={(e) => setFormData({...formData, subject: e.target.value})}
                  placeholder="e.g., Welcome to Delta AI Academy"
                  className="bg-[#0d0d0d] border-[#222] text-white"
                  data-testid="input-template-subject"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-400">HTML Content</Label>
              <Textarea
                value={formData.html_content}
                onChange={(e) => setFormData({...formData, html_content: e.target.value})}
                placeholder="Enter HTML content..."
                className="bg-[#0d0d0d] border-[#222] text-white font-mono text-xs min-h-[300px]"
                data-testid="input-template-html"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_default"
                checked={formData.is_default}
                onChange={(e) => setFormData({...formData, is_default: e.target.checked})}
                className="rounded border-[#222] bg-[#0d0d0d]"
              />
              <Label htmlFor="is_default" className="text-slate-400 cursor-pointer">
                Set as default template
              </Label>
            </div>
            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className="flex-1 border-[#222] text-slate-400"
              >
                Cancel
              </Button>
              <Button
                onClick={selectedTemplate ? handleUpdate : handleCreate}
                disabled={saving}
                className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white"
                data-testid="btn-save-template"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    {selectedTemplate ? "Update" : "Create"}
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="bg-[#111] border-[#1a1a1a] text-white max-w-3xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              Preview: {selectedTemplate?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4 bg-slate-200 rounded-lg overflow-hidden">
            <iframe
              srcDoc={selectedTemplate?.html_content}
              title="Template Preview"
              className="w-full h-[500px] border-0"
              sandbox="allow-same-origin"
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Templates;
